// `configureTemplateStreamTransport` — the injected-transport seam for the
// template-generation SSE stream, modelled on `src/data/config.ts`.
//
// The stream itself is domain work: which endpoint, which wire format, which
// frames. What it cannot own is the five app legs that turn that work into a
// request — the region-resolved origin, the synchronous auth-token read, the
// on-prem runtime predicate, the shared scribe SSE header builder and the
// shared non-OK `onopen` handler. Each is app or platform state by
// construction (Redux and jotai reads, a Tauri check, a build-time profile
// constant, an i18n-free error parser that logs), so each crosses here instead
// of moving.
//
// FIVE MEMBERS, one per leg, and every signature is the real app module's
// rather than a convenient narrowing: a widened parameter here would let the
// app bind something the stream cannot actually use and fail at runtime.
//
// Module scope is a `let` and three functions on purpose. This module is
// reached through the `.` door, so anything evaluated at import time is
// evaluated by every consumer of that door.

/**
 * The app legs the template-generation stream holds today, as a port.
 *
 * Structural throughout — no axios, no Tauri and no app type reaches this
 * file, so the door stays inert and the declarations stay portable.
 */
export interface TemplateStreamTransport {
  /**
   * Region-resolved ml-scribe origin for an endpoint version, terminated with
   * a slash. Bound to `@/helpers/getDomains`, which reads the region from the
   * app's resolution chain.
   */
  getDomain: (endPointVersion: 'v1' | 'v2' | 'v2-1' | 'v2-2') => string;
  /**
   * The synchronous access-token read. SYNC BY CONTRACT — the header builder
   * below returns a plain record, so there is nothing here to await into.
   * `null` and `undefined` are both real returns (suppressed cache, warm-up in
   * progress) and neither is evidence of a logout, so the stream passes
   * whatever comes back straight to `buildScribeSseHeaders`.
   */
  getAuthTokenForHttpClientSync: () => string | null | undefined;
  /**
   * True only in the on-prem desktop app, where the gateway's CORS policy
   * allows essential request headers only. The stream reads it to decide
   * whether the v1 wire-format opt-in header may be sent at all.
   */
  isOnPremRuntime: () => boolean;
  /**
   * The shared raw-fetch/SSE header builder for ml-scribe. It owns the bearer
   * construction, the client-identity and workspace headers, and the on-prem
   * metadata omission; the stream supplies only the token and its own extras.
   */
  buildScribeSseHeaders: (
    token: string | null | undefined,
    extra?: Record<string, string>
  ) => Record<string, string>;
  /**
   * The shared `onopen` handler. Non-OK responses are logged and reported
   * through `onError` WITHOUT throwing, which is what keeps the event-source
   * library's retry path unchanged; OK responses delegate to the caller's
   * `onOpen`.
   */
  handleStreamOpen: (args: {
    response: Response;
    logPrefix: string;
    onOpen?: (response: Response) => Promise<void> | void;
    onError?: (error: unknown) => void;
    context?: Record<string, unknown>;
  }) => Promise<void>;
}

let transport: TemplateStreamTransport | null = null;

/**
 * The unset failure, named so a caller can recognise it without matching on
 * message text.
 */
const templateStreamTransportUnsetError = (): Error => {
  const error = new Error(
    '[templates/template-streaming] transport not configured — app bootstrap ' +
      'must call configureTemplateStreamTransport() (see ' +
      'src/utils/templates-data-wiring.ts); tests exercising the real stream ' +
      'must configure a test double first.'
  );
  error.name = 'TemplateStreamTransportUnsetError';
  return error;
};

export const configureTemplateStreamTransport = (next: TemplateStreamTransport): void => {
  transport = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getTemplateStreamTransport = (): TemplateStreamTransport => {
  if (!transport) {
    throw templateStreamTransportUnsetError();
  }
  return transport;
};
